import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Seo from '../components/Seo';
import EnrollModal from '../components/EnrollModal';
import PriceTag from '../components/PriceTag';
import aptitudeCoverImg from '../images/aptitude-cover.svg';

// cardKey/defaultPrice here double as the fallback shown until /card-pricing
// loads, and as the labels the admin "Edit Card Price" page lists.
export const PROGRAM = {
  cardKey: 'dsa',
  title: 'Java DSA Mastery Program',
  desc: '75 hand-picked LeetCode problems, organized by pattern — Arrays, Hashing, and onward through Dynamic Programming and Graphs — every approach solved end-to-end in Java, cross-checked against real TCS and Infosys interview questions so you\'re not just solving famous problems, you\'re solving the ones you\'ll actually face.',
  defaultPrice: 149,
  image: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?q=80&w=800&auto=format&fit=crop',
};

export const APTITUDE_PROGRAM = {
  cardKey: 'aptitude',
  title: 'Aptitude Practice — Part 1',
  desc: '400 hand-verified Quantitative Aptitude questions across all 16 topics — Percentages through Data Interpretation. Every question comes with a real step-by-step solution, an official shortcut method, and space to share your own faster way to solve it.',
  defaultPrice: 49,
  image: aptitudeCoverImg,
};

const DecorativeBubbles = () => (
  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
    <div style={{ position: 'absolute', top: '20%', left: '-40px', width: '120px', height: '120px', borderRadius: '50%', background: 'var(--v1)', opacity: 0.15 }} />
    <div style={{ position: 'absolute', top: '35%', left: '40px', width: '40px', height: '40px', borderRadius: '50%', border: '3px solid var(--v2)', opacity: 0.25 }} />
    <div style={{ position: 'absolute', top: '15%', right: '-30px', width: '140px', height: '140px', borderRadius: '50%', border: '4px solid var(--v1)', opacity: 0.2 }} />
    <div style={{ position: 'absolute', bottom: '20%', right: '-10px', width: '90px', height: '90px', borderRadius: '50%', border: '2px solid var(--v2)', opacity: 0.25 }} />
  </div>
);

const Dsa = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', transactionId: '' });
  const [screenshot, setScreenshot] = useState(null);
  const [upiCopied, setUpiCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [myStatus, setMyStatus] = useState(null); // existing enrollment, if any
  const [adminEnrolling, setAdminEnrolling] = useState(false);
  const [pricing, setPricing] = useState({});

  useEffect(() => {
    if (!user) return;
    API.get('/dsa/my-status').then(r => setMyStatus(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    API.get('/card-pricing').then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const dsaPrice = { oldPrice: pricing[PROGRAM.cardKey]?.oldPrice ?? null, newPrice: pricing[PROGRAM.cardKey]?.newPrice ?? PROGRAM.defaultPrice };
  const aptPrice = { oldPrice: pricing[APTITUDE_PROGRAM.cardKey]?.oldPrice ?? null, newPrice: pricing[APTITUDE_PROGRAM.cardKey]?.newPrice ?? APTITUDE_PROGRAM.defaultPrice };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleFileChange = (e) => setScreenshot(e.target.files[0]);

  const goToLogin = () => navigate('/login', { state: { from: '/dsa' } });

  const adminInstantAccess = async () => {
    setAdminEnrolling(true);
    try {
      const res = await API.post('/dsa/admin-enroll');
      navigate(`/dsa-workspace/${res.data.enrollmentId}?email=${encodeURIComponent(user.email)}`);
    } catch (err) {
      console.error(err);
      setAdminEnrolling(false);
    }
  };

  const openProgram = () => {
    if (myStatus) {
      navigate(`/dsa-workspace/${myStatus.enrollmentId}?email=${encodeURIComponent(user.email)}`);
      return;
    }
    if (user?.role === 'admin') { adminInstantAccess(); return; }
    if (user) {
      setFormData(f => ({ ...f, name: f.name || user.name || '', email: f.email || user.email || '' }));
    }
    setShowModal(true);
  };

  const closeModal = () => {
    if (!loading) { setShowModal(false); setError(null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { setError('Please login or sign up to continue.'); return; }
    if (!screenshot) { setError('Please upload the payment screenshot.'); return; }
    setLoading(true); setError(null);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => data.append(key, formData[key]));
      data.append('paymentScreenshot', screenshot);

      const res = await API.post('/dsa/enroll', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      navigate(`/dsa-workspace/${res.data.enrollmentId}?email=${encodeURIComponent(formData.email)}`);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyUpi = () => {
    navigator.clipboard.writeText('yeeswanthlatha-1@okaxis');
    setUpiCopied(true);
    setTimeout(() => setUpiCopied(false), 1800);
  };

  // ── Aptitude Practice enrollment (mirrors the DSA flow above) ──
  const [showAptModal, setShowAptModal] = useState(false);
  const [aptFormData, setAptFormData] = useState({ name: '', email: '', transactionId: '' });
  const [aptScreenshot, setAptScreenshot] = useState(null);
  const [aptUpiCopied, setAptUpiCopied] = useState(false);
  const [aptLoading, setAptLoading] = useState(false);
  const [aptError, setAptError] = useState(null);
  const [aptMyStatus, setAptMyStatus] = useState(null);
  const [aptAdminEnrolling, setAptAdminEnrolling] = useState(false);

  useEffect(() => {
    if (!user) return;
    API.get('/aptitude/my-status').then(r => setAptMyStatus(r.data)).catch(() => {});
  }, [user]);

  const handleAptChange = (e) => setAptFormData({ ...aptFormData, [e.target.name]: e.target.value });
  const handleAptFileChange = (e) => setAptScreenshot(e.target.files[0]);

  const aptAdminInstantAccess = async () => {
    setAptAdminEnrolling(true);
    try {
      const res = await API.post('/aptitude/admin-enroll');
      navigate(`/aptitude-workspace/${res.data.enrollmentId}?email=${encodeURIComponent(user.email)}`);
    } catch (err) {
      console.error(err);
      setAptAdminEnrolling(false);
    }
  };

  const openAptitudeProgram = () => {
    if (aptMyStatus) {
      navigate(`/aptitude-workspace/${aptMyStatus.enrollmentId}?email=${encodeURIComponent(user.email)}`);
      return;
    }
    if (user?.role === 'admin') { aptAdminInstantAccess(); return; }
    if (user) {
      setAptFormData(f => ({ ...f, name: f.name || user.name || '', email: f.email || user.email || '' }));
    }
    setShowAptModal(true);
  };

  const closeAptModal = () => {
    if (!aptLoading) { setShowAptModal(false); setAptError(null); }
  };

  const handleAptSubmit = async (e) => {
    e.preventDefault();
    if (!user) { setAptError('Please login or sign up to continue.'); return; }
    if (!aptScreenshot) { setAptError('Please upload the payment screenshot.'); return; }
    setAptLoading(true); setAptError(null);
    try {
      const data = new FormData();
      Object.keys(aptFormData).forEach(key => data.append(key, aptFormData[key]));
      data.append('paymentScreenshot', aptScreenshot);

      const res = await API.post('/aptitude/enroll', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      navigate(`/aptitude-workspace/${res.data.enrollmentId}?email=${encodeURIComponent(aptFormData.email)}`);
    } catch (err) {
      console.error(err);
      setAptError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setAptLoading(false);
    }
  };

  const copyAptUpi = () => {
    navigator.clipboard.writeText('yeeswanthlatha-1@okaxis');
    setAptUpiCopied(true);
    setTimeout(() => setAptUpiCopied(false), 1800);
  };

  return (
    <>
      <style>{`
        .dsa-page { background: var(--bg); color: var(--text); min-height: 100vh; font-family: 'Satoshi', 'Inter', system-ui, sans-serif; padding-bottom: 100px; }
        .hero-section { text-align: center; padding: 100px 20px 60px; }
        .hero-title { font-family: 'Inter', sans-serif; font-size: clamp(36px, 5vw, 64px); font-weight: 800; letter-spacing: -0.02em; margin-bottom: 24px; line-height: 1.1; }
        .hero-title span { background: var(--grad-brand); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .hero-subtitle { font-size: clamp(16px, 2vw, 18px); color: var(--text-2); max-width: 600px; margin: 0 auto; line-height: 1.6; }

        .dsa-card-wrap {
          max-width: 1120px; margin: 0 auto; padding: 0 20px;
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 28px; align-items: stretch;
        }
        @media (max-width: 860px) {
          .dsa-card-wrap { grid-template-columns: 1fr; max-width: 560px; }
        }
        .dsa-card {
          background: var(--card); backdrop-filter: blur(12px); border-radius: 20px;
          overflow: hidden; color: var(--text); cursor: pointer;
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease;
          display: flex; flex-direction: column; position: relative;
        }
        .dsa-card:hover { transform: translateY(-8px); box-shadow: var(--shadow-hover); border: 1px solid var(--border-strong); }
        .dsa-card-img-wrapper { width: 100%; height: 260px; overflow: hidden; position: relative; }
        .dsa-card-lang-badge {
          position: absolute; top: 16px; left: 16px; z-index: 2;
          display: inline-flex; align-items: center; gap: 6px;
          background: linear-gradient(135deg,#7F00FF,#B163FF); color: #fff;
          font-size: 12.5px; font-weight: 800; letter-spacing: 0.02em;
          padding: 7px 14px; border-radius: 999px;
          box-shadow: 0 8px 20px rgba(127,0,255,0.35);
        }
        .dsa-card-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease; }
        .dsa-card:hover .dsa-card-img { transform: scale(1.05); }
        .dsa-card-content { padding: 32px; display: flex; flex-direction: column; flex: 1; }
        .dsa-card-title { font-family: 'Inter', sans-serif; font-size: 26px; font-weight: 700; margin-bottom: 14px; line-height: 1.3; }
        .dsa-card-desc { color: var(--muted); font-size: 15px; line-height: 1.7; margin-bottom: 28px; }
        .dsa-card-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 20px; border-top: 1px solid var(--border); margin-top: auto; }
        .dsa-card-price { font-family: 'Inter', sans-serif; font-size: 28px; font-weight: 800; }
        .dsa-card-btn { background: var(--bg); color: var(--text); padding: 10px 24px; border-radius: 99px; font-size: 14.5px; font-weight: 600; transition: background 0.2s; }
        .dsa-card:hover .dsa-card-btn { background: var(--v2); color: #fff; }

        .modal-overlay { animation: fadeIn 0.3s ease-out forwards; position: fixed; inset: 0; z-index: 5000; background: rgba(0,0,0,0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 20px; }
        .modal-content { animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; background: var(--card); backdrop-filter: blur(12px); border-radius: 24px; width: 100%; max-width: 960px; max-height: 90vh; overflow: hidden; display: flex; flex-direction: column; color: var(--text); box-shadow: var(--shadow-lg); position: relative; }
        .login-banner { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; background: linear-gradient(135deg, rgba(127,0,255,0.14), rgba(177,99,255,0.09)); border-bottom: 1px solid var(--border); padding: 16px 56px 16px 24px; flex-shrink: 0; }
        .login-banner-text { font-size: 14px; font-weight: 600; color: var(--text); }
        .login-banner-btn { background: var(--v1); color: #fff; border: none; padding: 10px 20px; border-radius: 8px; font-weight: 700; font-size: 13.5px; cursor: pointer; white-space: nowrap; font-family: inherit; transition: opacity 0.2s; }
        .login-banner-btn:hover { opacity: 0.88; }
        .modal-columns { display: flex; flex: 1; min-height: 0; }
        .modal-left { width: 45%; background: var(--bg2); padding: 40px; border-right: 1px solid var(--border); display: flex; flex-direction: column; overflow-y: auto; }
        .modal-right { width: 55%; padding: 40px; overflow-y: auto; }
        .modal-course-img { width: 100%; height: 180px; object-fit: cover; border-radius: 12px; margin-bottom: 24px; }

        @media (max-width: 768px) {
          .modal-overlay { padding: 0; align-items: flex-end; }
          .modal-content { max-height: 96vh; border-radius: 24px 24px 0 0; }
          .login-banner { padding: 14px 50px 14px 16px; }
          .login-banner-text { font-size: 13px; }
          .modal-columns { flex-direction: column; overflow-y: auto; }
          .modal-left, .modal-right { width: 100% !important; padding: 24px !important; }
          .modal-left { border-right: none !important; border-bottom: 1px solid var(--border); }
          .modal-course-img { height: 130px; margin-bottom: 16px; }
        }
        .close-btn { position: absolute; top: 20px; right: 20px; background: var(--bg3); border: none; width: 36px; height: 36px; border-radius: 50%; color: var(--text); font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 10; transition: background 0.2s; }
        .close-btn:hover { background: var(--bg4); }

        .qr-box { background: var(--card-solid); border: 1px solid var(--border); padding: 20px; border-radius: 16px; margin-bottom: 16px; display: flex; flex-direction: column; align-items: center; gap: 14px; width: 100%; max-width: 260px; }
        .qr-img { width: 100%; max-width: 200px; aspect-ratio: 1 / 1; object-fit: contain; border-radius: 10px; border: 1px solid var(--border); background: #fff; padding: 8px; }
        .upi-tag { display: flex; flex-direction: column; align-items: center; gap: 3px; background: rgba(127,0,255,0.08); border: 1px solid rgba(127,0,255,0.25); padding: 9px 16px; border-radius: 999px; width: 100%; cursor: pointer; font-family: inherit; transition: background 0.2s, border-color 0.2s; }
        .upi-tag:hover { background: rgba(127,0,255,0.14); border-color: rgba(127,0,255,0.4); }
        .upi-tag-label { font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted); }
        .upi-tag-value { font-size: 13.5px; font-weight: 700; color: var(--v2); word-break: break-all; }
        .contact-note { font-size: 13px; color: var(--muted); margin-bottom: 8px; }
        .contact-note a { color: var(--v2); font-weight: 700; text-decoration: none; }
        .contact-note a:hover { text-decoration: underline; }
        @media (max-width: 768px) { .qr-box { max-width: 100%; } }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(40px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>

      <Seo
        title="Java DSA Mastery Program"
        description="75 hand-picked LeetCode problems, organized by pattern, every solution in Java, cross-checked against real TCS and Infosys interview questions — for just ₹149."
        path="/dsa"
      />
      <div className="dsa-page">
        <section className="hero-section" style={{ position: 'relative', overflow: 'hidden' }}>
          <DecorativeBubbles />
          <div style={{ position: 'relative', zIndex: 2 }}>
            <h1 className="hero-title">
              The <span>Java DSA</span> Mastery Program
            </h1>
            <p className="hero-subtitle">
              Stop guessing what to practice. 75 curated problems, organized by pattern, solved end-to-end in Java, built to get you interview-ready.
            </p>
          </div>
        </section>

        <section className="dsa-card-wrap">
          <div className="dsa-card" onClick={openProgram} style={adminEnrolling ? { opacity: 0.6, cursor: 'wait' } : undefined}>
            <div className="dsa-card-img-wrapper">
              <span className="dsa-card-lang-badge">☕ Java</span>
              <img src={PROGRAM.image} alt={PROGRAM.title} className="dsa-card-img" />
            </div>
            <div className="dsa-card-content">
              <h3 className="dsa-card-title">{PROGRAM.title}</h3>
              <p className="dsa-card-desc">{PROGRAM.desc}</p>
              <div className="dsa-card-footer">
                <PriceTag oldPrice={dsaPrice.oldPrice} newPrice={dsaPrice.newPrice} className="dsa-card-price" oldSize={16} />
                <span className="dsa-card-btn">
                  {adminEnrolling ? 'Opening…' : myStatus ? 'Continue →' : (user?.role === 'admin' ? 'Open (Admin)' : 'Enroll Now')}
                </span>
              </div>
            </div>
          </div>

          <div className="dsa-card" onClick={openAptitudeProgram} style={aptAdminEnrolling ? { opacity: 0.6, cursor: 'wait' } : undefined}>
            <div className="dsa-card-img-wrapper">
              <span className="dsa-card-lang-badge">
                <img src="/favicon-32.png" alt="" style={{ width: 15, height: 15, borderRadius: 4, display: 'block' }} /> Aptitude
              </span>
              <img src={APTITUDE_PROGRAM.image} alt={APTITUDE_PROGRAM.title} className="dsa-card-img" />
            </div>
            <div className="dsa-card-content">
              <h3 className="dsa-card-title">{APTITUDE_PROGRAM.title}</h3>
              <p className="dsa-card-desc">{APTITUDE_PROGRAM.desc}</p>
              <div className="dsa-card-footer">
                <PriceTag oldPrice={aptPrice.oldPrice} newPrice={aptPrice.newPrice} className="dsa-card-price" oldSize={16} />
                <span className="dsa-card-btn">
                  {aptAdminEnrolling ? 'Opening…' : aptMyStatus ? 'Continue →' : (user?.role === 'admin' ? 'Open (Admin)' : 'Enroll Now')}
                </span>
              </div>
            </div>
          </div>
        </section>

        {showModal && (
          <EnrollModal
            program={{ ...PROGRAM, price: `₹${dsaPrice.newPrice}` }}
            user={user}
            onClose={closeModal}
            onLogin={goToLogin}
            formData={formData}
            onChange={handleChange}
            onFileChange={handleFileChange}
            upiCopied={upiCopied}
            onCopyUpi={copyUpi}
            error={error}
            loading={loading}
            onSubmit={handleSubmit}
          />
        )}

        {showAptModal && (
          <EnrollModal
            program={{ ...APTITUDE_PROGRAM, price: `₹${aptPrice.newPrice}` }}
            user={user}
            onClose={closeAptModal}
            onLogin={goToLogin}
            formData={aptFormData}
            onChange={handleAptChange}
            onFileChange={handleAptFileChange}
            upiCopied={aptUpiCopied}
            onCopyUpi={copyAptUpi}
            error={aptError}
            loading={aptLoading}
            onSubmit={handleAptSubmit}
          />
        )}
      </div>
    </>
  );
};

export default Dsa;
